import React, { ReactNode, useMemo } from 'react';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getFullName } from '@frontend/name';
import { contextFactory } from '@frontend/design-system';
import { useGetPractitionersV3Data, useGetProvidersListForMultipleLocationIds } from '../hooks';
import { useCalendarLocationIds } from '../stores';
import type { CalendarEventContextType } from '../types';
import { CALENDAR_VIEW_V3_API_QUERY_LIMIT, transformV3ResponsesForReviewRequest } from '../utils';

// context
export const [CalendarEventContext, useCalendarEvent] = contextFactory<CalendarEventContextType>();

// provider
interface CalendarEventProviderProps {
  children: ReactNode;
}

export const CalendarEventProvider = React.memo(({ children }: CalendarEventProviderProps) => {
  const { calendarPageLocationIds } = useCalendarLocationIds();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  // Fetch V2 providers data
  const { data: providersV2 } = useGetProvidersListForMultipleLocationIds({ locationIds: calendarPageLocationIds });

  // Fetch V3 providers data
  const { data: providersV3 } = useGetPractitionersV3Data({
    selectedLocationId: calendarPageLocationIds[0],
    selectedLocationIds: calendarPageLocationIds,
    pageConfig: { page: 1, limit: CALENDAR_VIEW_V3_API_QUERY_LIMIT },
  });

  const providerListV3 = useMemo(
    () =>
      transformV3ResponsesForReviewRequest({
        practitionerDataV3: providersV3?.providers,
      }).transformedPractitioners as Provider[],
    [providersV3?.providers]
  );

  const value = useMemo(() => {
    const providerList = (isScheduleV3FlagEnabled ? providerListV3 : providersV2) || [];
    const sortedProviders = providerList.sort((a, b) => {
      const aFullName = getFullName(a).toLowerCase();
      const bFullName = getFullName(b).toLowerCase();
      return aFullName.localeCompare(bFullName);
    });

    return {
      selectedLocationIds: calendarPageLocationIds,
      providersList: sortedProviders,
    };
  }, [isScheduleV3FlagEnabled, calendarPageLocationIds, providerListV3, providersV2]);

  return <CalendarEventContext.Provider value={value}>{children}</CalendarEventContext.Provider>;
});

CalendarEventProvider.displayName = 'CalendarEventProvider';
